<template>
  <div class="hidden">
    <video
      ref="videoElement"
      class="videoBackground"
      v-if="isVideo"
      loop
      muted
      :src="url"
      crossorigin="anonymous"
    />
    <div
      class="imageBackground"
      :class="{
        animate: animate,
        noAnimation: stopAnimationWhenNotPlaying && !animate,
      }"
      v-if="!isVideo"
      :style="'background-image:url(' + url + ');'"
    ></div>
    <div class="overlay" v-if="mask !== 'none'" :class="mask"></div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";

@Component({})
export default class VideoImageBackground extends Vue {
  @Ref("videoElement")
  private videoElement!: HTMLVideoElement;

  @Prop({ required: true })
  private url!: string;

  @Prop({ default: "none" })
  private mask!: "none" | "light" | "dark";

  @Prop({ default: false })
  private animate!: boolean;

  @Prop({ default: false })
  private stopAnimationWhenNotPlaying!: boolean;

  @Watch("url", { immediate: true })
  private onUrlChange() {
    this.$nextTick(this.onAnimateChange);
  }

  get isVideo(): boolean {
    try {
      const path = new URL(this.url).pathname;
      return path.endsWith(".mp4") || path.endsWith(".webm");
    } catch {
      return this.url.endsWith(".mp4") || this.url.endsWith(".webm");
    }
  }

  @Watch("animate", { immediate: true })
  private onAnimateChange() {
    if (this.isVideo && this.videoElement) {
      if (this.animate) {
        this.videoElement.play();
      } else {
        this.videoElement.pause();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.hidden {
  overflow: hidden;
  position: absolute;
  width: 100%;
  height: 100%;
  background: url("@/assets/videoBackground/videoBackground.jpg");
  background-size: cover;
}
.videoBackground {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  pointer-events: none;
}
.imageBackground {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  z-index: 0;
  pointer-events: none;
  will-change: transform;
  animation-name: bokehPulse;
  animation-duration: 20s;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
  animation-play-state: paused;
  &.animate {
    animation-play-state: running;
  }
  &.noAnimation {
    animation-name: none;
  }
}
.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  &.light {
    background: rgba(255, 255, 255, 0.8);
  }
  &.dark {
    background: rgba(0, 0, 0, 0.8);
  }
}

@keyframes bokehPulse {
  0% {
    transform: scale(1.3) translate(0%, 0%);
  }
  50% {
    transform: scale(1.2) translate(5%, -5%);
  }
  100% {
    transform: scale(1.3) translate(0%, 0%);
  }
}
</style>
